import type { ReactNode } from "react"

import { Switch } from "@f2e/ui/components/base"

export interface ConfigurableFieldShellProps {
  id: string
  title: string
  description?: string
  enabled: boolean
  required?: boolean
  disabled?: boolean
  loading?: boolean
  summary?: ReactNode
  error?: string
  children: ReactNode
  onEnabledChange: (enabled: boolean) => void
}

export const ConfigurableFieldShell = ({
  id,
  title,
  description,
  enabled,
  required,
  disabled,
  loading,
  summary,
  error,
  children,
  onEnabledChange,
}: ConfigurableFieldShellProps) => {
  const descriptionId = description ? `${id}-description` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [descriptionId, errorId].filter(Boolean).join(" ")
  return (
    <section
      className="space-y-4 rounded-xl border border-border p-4"
      aria-labelledby={`${id}-title`}
      aria-busy={loading || undefined}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <label
            id={`${id}-title`}
            htmlFor={required ? undefined : `${id}-enabled`}
            className="text-sm font-medium"
          >
            {title}
            {required ? " *" : ""}
          </label>
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {summary && (
            <div className="text-sm text-muted-foreground">{summary}</div>
          )}
          {error && (
            <p id={errorId} className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        {!required && (
          <Switch
            id={`${id}-enabled`}
            checked={enabled}
            disabled={disabled || loading}
            aria-invalid={!!error}
            aria-describedby={describedBy || undefined}
            onCheckedChange={onEnabledChange}
          />
        )}
      </div>
      {(required || enabled) && !loading && <div>{children}</div>}
    </section>
  )
}
